import React, { useState, useEffect } from 'react';
import { db } from '../../firebase';
import { collection, doc, getDoc, getDocs, query, orderBy, setDoc, updateDoc, Timestamp, onSnapshot } from "firebase/firestore";
import { Link, useNavigate } from 'react-router-dom';

const months = [
    { value: "01", name: "January" }, { value: "02", name: "February" }, { value: "03", name: "March" },
    { value: "04", name: "April" }, { value: "05", name: "May" }, { value: "06", name: "June" },
    { value: "07", name: "July" }, { value: "08", name: "August" }, { value: "09", name: "September" },
    { value: "10", name: "October" }, { value: "11", name: "November" }, { value: "12", name: "December" }
];
const currentYear = new Date().getFullYear();
const years = [currentYear, currentYear + 1, currentYear - 1]; 
const formatCurrency = (num) => {
  if (typeof num !== 'number') { num = parseFloat(num || 0); }
  return new Intl.NumberFormat('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(num);
};

const getNextReceiptNo = async () => {
    const counterRef = doc(db, "counters", "paymentCounter");
    const counterSnap = await getDoc(counterRef);
    let newReceiptNo;
    if (!counterSnap.exists()) {
        newReceiptNo = 1001; 
        await setDoc(counterRef, { lastReceiptNo: newReceiptNo });
    } else {
        const lastReceiptNo = counterSnap.data().lastReceiptNo;
        newReceiptNo = lastReceiptNo + 1;
        await updateDoc(counterRef, { lastReceiptNo: newReceiptNo });
    }
    return newReceiptNo; 
};

function numberToWords(num) {
    const a = ['','One ','Two ','Three ','Four ', 'Five ','Six ','Seven ','Eight ','Nine ','Ten ','Eleven ','Twelve ','Thirteen ','Fourteen ','Fifteen ','Sixteen ','Seventeen ','Eighteen ','Nineteen '];
    const b = ['', '', 'Twenty','Thirty','Forty','Fifty', 'Sixty','Seventy','Eighty','Ninety'];
    const inWordsBDTInternal = (n) => {
        let str = '';
        if (n > 9999999) { str += inWordsBDTInternal(Math.floor(n / 10000000)); str += 'Crore '; n %= 10000000; }
        if (n > 99999) { str += inWordsBDTInternal(Math.floor(n / 100000)); str += 'Lakh '; n %= 100000; }
        if (n > 999) { str += inWordsBDTInternal(Math.floor(n / 1000)); str += 'Thousand '; n %= 1000; }
        if (n > 99) { str += inWordsBDTInternal(Math.floor(n / 100)); str += 'Hundred '; n %= 100; }
        if (n > 19) { str += b[Math.floor(n / 10)] + ' ' + a[n % 10]; } else { str += a[n]; }
        return str; 
    };
    const taka = Math.round(parseFloat(num || 0));
    if (taka === 0) return 'BDT ZERO TAKA ONLY';
    let takaWords = inWordsBDTInternal(taka).trim(); 
    let result = 'BDT ' + takaWords + ' Taka Only'; 
    return result.replace(/\s+/g, ' ').toUpperCase();
}

// --- NEW FUNCTION: Update Future Invoices Automatically ---
const updateFutureInvoices = async (companyId, startYear, startMonth, startFinalTotal) => {
    let currentYear = parseInt(startYear);
    let currentMonth = parseInt(startMonth);
    let currentPreviousDue = startFinalTotal;

    // Loop through next months to propagate the balance
    while (true) {
        // Increment month
        currentMonth++;
        if (currentMonth > 12) {
            currentMonth = 1;
            currentYear++;
        }
        
        const monthStr = String(currentMonth).padStart(2, '0');
        const nextDocId = `${companyId}_${currentYear}_${monthStr}`;
        const nextRef = doc(db, "invoices", nextDocId);
        
        try {
            const nextSnap = await getDoc(nextRef);
            if (!nextSnap.exists()) {
                // If no invoice found for this next month, stop the chain
                break;
            }
            
            const data = nextSnap.data();
            
            // Recalculate Final Total for this future invoice
            // New Final Total = (Total BDT of that month + New Previous Due) - Total Paid - Discount
            const newFinalTotal = (parseFloat(data.totalBdt || 0) + parseFloat(currentPreviousDue)) - parseFloat(data.totalPaid || 0) - parseFloat(data.discountAmount || 0);
            
            await updateDoc(nextRef, {
                previousDue: parseFloat(currentPreviousDue),
                finalTotalBdt: newFinalTotal
            });
            
            console.log(`Auto-updated invoice: ${nextDocId}`);
            
            // Set the new total as the "Previous Due" for the *next* iteration
            currentPreviousDue = newFinalTotal;
            
        } catch (err) {
            console.error("Error updating future invoice chain:", err);
            break; 
        }
    }
};

const Payments = () => {
    const navigate = useNavigate();
    const [companyList, setCompanyList] = useState([]);
    const [loading, setLoading] = useState(false); 
    const [listLoading, setListLoading] = useState(true);
    const [message, setMessage] = useState('');
    const [recentPayments, setRecentPayments] = useState([]); 
    const [searchQuery, setSearchQuery] = useState('');

    const [selectedCompanyId, setSelectedCompanyId] = useState('');
    const [selectedBillMonth, setSelectedBillMonth] = useState('');
    const [selectedBillYear, setSelectedBillYear] = useState(currentYear);
    
    const [paymentEntries, setPaymentEntries] = useState([]); 
    const [currentAmount, setCurrentAmount] = useState('');
    const [currentMethod, setCurrentMethod] = useState('Cash');
    const [currentEntryRemarks, setCurrentEntryRemarks] = useState(''); 

    const [paymentDate, setPaymentDate] = useState(new Date().toISOString().split('T')[0]);
    // 👇 নতুন স্টেট: Amount Due Date এর জন্য
    const [amountDueDate, setAmountDueDate] = useState(''); 
    const [authorizedBy, setAuthorizedBy] = useState('MD. Morshed');

    useEffect(() => {
        const fetchCompanies = async () => {
            try {
                const q = query(collection(db, 'companies'), orderBy('name'));
                const querySnapshot = await getDocs(q);
                const list = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                setCompanyList(list);
            } catch (err) { console.error("Error fetching companies: ", err); }
        };
        fetchCompanies();
        setListLoading(true);
        const q = query(collection(db, "payments"), orderBy("paymentTimestamp", "desc"));
        const unsubscribe = onSnapshot(q, (querySnapshot) => {
            const paymentsList = querySnapshot.docs.map(doc => ({ ...doc.data(), id: doc.id }));
            setRecentPayments(paymentsList);
            setListLoading(false);
        }, (error) => {
            console.error("Error fetching payments real-time: ", error);
            setListLoading(false);
        });
        return () => unsubscribe();
    }, []); 

    const handleAddEntry = () => {
        if (!currentAmount || parseFloat(currentAmount) <= 0) {
            alert("Please enter a valid amount.");
            return;
        }
        setPaymentEntries(prev => [
            ...prev,
            {
                id: Date.now(),
                amount: parseFloat(currentAmount),
                method: currentMethod,
                remarks: currentEntryRemarks 
            }
        ]);
        setCurrentAmount('');
        setCurrentMethod('Cash');
        setCurrentEntryRemarks(''); 
    };

    const handleRemoveEntry = (id) => {
        setPaymentEntries(prev => prev.filter(entry => entry.id !== id));
    };

    const totalAmount = paymentEntries.reduce((acc, entry) => acc + entry.amount, 0);
    
    const handleSavePayment = async (e) => {
        e.preventDefault();
        if (!selectedCompanyId || !selectedBillMonth || !selectedBillYear) {
            setMessage('Please fill Company, Bill Month, and Bill Year.'); return;
        }
        if (paymentEntries.length === 0) {
            setMessage('Please add at least one payment entry.'); return;
        }

        setLoading(true);
        setMessage('');

        const invoiceDocId = `${selectedCompanyId}_${selectedBillYear}_${selectedBillMonth}`;
        const invoiceRef = doc(db, "invoices", invoiceDocId);
        const companyData = companyList.find(c => c.id === selectedCompanyId);

        try {
            const docSnap = await getDoc(invoiceRef);
            if (!docSnap.exists()) {
                setMessage(`Error: No bill found for ${selectedBillMonth}/${selectedBillYear}. Please create the bill first.`);
                setLoading(false); return;
            }
            const invoiceData = docSnap.data();

            const newReceiptNo = await getNextReceiptNo(); 
            const formattedReceiptNo = `MR-${newReceiptNo}`; 
            const paymentDocRef = doc(collection(db, "payments"));
            const newPaymentDocId = paymentDocRef.id;
            
            const items = invoiceData.items || [];
            const exchangeRate = invoiceData.exchangeRate || 100;
            const previousDue = invoiceData.previousDue || 0;
            const discount = invoiceData.discountAmount || 0;
            const existingPaidEntries = invoiceData.paidEntries || [];

            const invoicePaymentEntry = {
                id: Date.now(), 
                paymentDocId: newPaymentDocId, 
                date: new Date(paymentDate).toLocaleDateString('en-GB'), 
                amount: totalAmount, 
                method: paymentEntries.length > 1 ? 'Multiple' : paymentEntries[0].method,
            };
            
            const updatedPaidEntries = [...existingPaidEntries, invoicePaymentEntry];
            
            // 👇 Date formatting logic
            let formattedAmountDueDate = null;
            if (amountDueDate) {
                const [y, m, d] = amountDueDate.split('-');
                formattedAmountDueDate = `${d}/${m}/${y}`;
            }

            const calculateItemTotal = (item) => {
                const rate = parseFloat(item.rate || 0); const fsc = parseFloat(item.fsc || 0);
                const demand = parseFloat(item.demandCharge || 0); const weight = parseFloat(item.weight || 0);
                return (rate + fsc + (demand * weight)); 
            };
            const totalUsd = invoiceData.totalUsd || items.reduce((acc, item) => acc + calculateItemTotal(item), 0);
            const totalBdt = invoiceData.totalBdt || (totalUsd * exchangeRate);
            
            let newTotalPaid;
            if (invoiceData.allowEditTotalPaid) {
                newTotalPaid = (invoiceData.totalPaid || 0) + totalAmount;
            } else {
                newTotalPaid = updatedPaidEntries.reduce((acc, entry) => acc + parseFloat(entry.amount || 0), 0);
            }
            
            const finalTotalBdt = (totalBdt + previousDue) - newTotalPaid - discount;
            
            const receiptData = {
                paymentEntries: paymentEntries.map(({id, ...rest}) => rest), 
                totalAmount: totalAmount, 
                receiptNo: formattedReceiptNo, 
                invoiceDocId: invoiceDocId, 
                companyId: selectedCompanyId,
                companyName: companyData?.name,
                customerName: companyData?.customerName,
                address: companyData?.address,
                phone: companyData?.phone,
                billMonth: `${months.find(m => m.value === selectedBillMonth)?.name} ${selectedBillYear}`,
                amountInWords: numberToWords(totalAmount), 
                paymentTimestamp: Timestamp.fromDate(new Date(paymentDate)),
                date: new Date(paymentDate).toLocaleDateString('en-GB'),
                // 👇 Save the optional date 
                amountDueDate: formattedAmountDueDate,
                finalTotalBdt: finalTotalBdt,
                authorizedBy: authorizedBy,
            };
            
            await setDoc(paymentDocRef, receiptData);
            
            await updateDoc(invoiceRef, { 
                paidEntries: updatedPaidEntries, 
                totalPaid: newTotalPaid,
                finalTotalBdt: finalTotalBdt
            });

            await updateFutureInvoices(selectedCompanyId, selectedBillYear, selectedBillMonth, finalTotalBdt);

            setMessage(`Payment added successfully! Receipt No: ${formattedReceiptNo}`);
            setPaymentEntries([]);
            setAuthorizedBy('MD. Morshed'); 
            setAmountDueDate(''); // Reset
            navigate(`/dashboard/payments/view/${newPaymentDocId}`);

        } catch (error) {
            console.error("Error adding payment: ", error);
            setMessage('Error: Could not add payment.');
        }
        setLoading(false);
    };

    const filteredPayments = recentPayments.filter(payment =>
        payment.receiptNo && String(payment.receiptNo).toLowerCase().includes(searchQuery.toLowerCase())
    );

    return (
        <div className='payment-container'>
            <p className='new-invoice-heading'>Log Payment & Create Money Receipt</p>
            <form onSubmit={handleSavePayment}>
                <div className='form-section'>
                    <h3>Step 1: Select Bill</h3>
                    <div className='form-grid' style={{gridTemplateColumns: '1fr 1fr 1fr'}}>
                        <div>
                            <label>Company:</label>
                            <select value={selectedCompanyId} onChange={e => setSelectedCompanyId(e.target.value)} required>
                                <option value="" disabled>-- Select Company --</option>
                                {companyList.map(comp => (<option key={comp.id} value={comp.id}>{comp.name}</option>))}
                            </select>
                        </div>
                        <div>
                            <label>Bill Month:</label>
                            <select value={selectedBillMonth} onChange={e => setSelectedBillMonth(e.target.value)} required>
                                <option value="" disabled>-- Select Month --</option>
                                {months.map(m => <option key={m.value} value={m.value}>{m.name}</option>)}
                            </select>
                        </div>
                        <div>
                            <label>Bill Year:</label>
                            <select value={selectedBillYear} onChange={e => setSelectedBillYear(e.target.value)} required>
                                {years.map(y => <option key={y} value={y}>{y}</option>)}
                            </select>
                        </div>
                    </div>
                </div>
                
                <div className='form-section'>
                    <h3>Step 2: Add Payment Entries</h3>
                    <div className='form-grid' style={{gridTemplateColumns: '1fr 1fr 1fr auto', gap: '15px', alignItems: 'flex-end'}}>
                        <div>
                            <label>Payment Method:</label>
                            <select value={currentMethod} onChange={e => setCurrentMethod(e.target.value)}>
                                <option value="Cash">Cash</option>
                                <option value="Cheque">Cheque</option>
                                <option value="Bank Transfer">Bank Transfer</option>
                            </select>
                        </div>
                        <div>
                            <label>Amount (BDT):</label>
                            <input type='number' value={currentAmount} onChange={e => setCurrentAmount(e.target.value)} placeholder='0.00' style={{textAlign: 'right'}} />
                        </div>
                        <div>
                            <label>Remarks (for this entry):</label>
                            <input type='text' value={currentEntryRemarks} onChange={e => setCurrentEntryRemarks(e.target.value)} placeholder='e.g., Cheque no 123' />
                        </div>
                        <button type="button" className='add-btn' onClick={handleAddEntry} style={{margin: 0}}>Add</button>
                    </div>

                    {paymentEntries.length > 0 && (
                        <table className='invoice-preview-table' style={{marginTop: '20px', fontSize: '14px'}}>
                            <thead>
                                <tr><th>Method</th><th>Remarks</th><th>Amount</th><th>Action</th></tr>
                            </thead>
                            <tbody>
                                {paymentEntries.map(entry => (
                                    <tr key={entry.id}>
                                        <td>{entry.method}</td><td>{entry.remarks}</td><td style={{textAlign: 'right'}}>{formatCurrency(entry.amount)}</td>
                                        <td style={{textAlign: 'center'}}><button type="button" onClick={() => handleRemoveEntry(entry.id)} className='remove-btn-small'>X</button></td>
                                    </tr>
                                ))}
                                <tr><td colSpan="2" style={{textAlign: 'right', fontWeight: 'bold'}}>TOTAL:</td><td style={{textAlign: 'right', fontWeight: 'bold'}}>{formatCurrency(totalAmount)}</td><td></td></tr>
                            </tbody>
                        </table>
                    )}
                </div>
                
                <div className='form-section'>
                    <h3>Step 3: Receipt Details</h3>
                    <div className='form-grid' style={{gridTemplateColumns: '1fr 1fr 1fr'}}>
                        <div>
                            <label>Payment Date (For Receipt):</label>
                            <input type='date' value={paymentDate} onChange={e => setPaymentDate(e.target.value)} required />
                        </div>
                        {/* 👇 এখানে নতুন ডেট ইনপুট */}
                        <div>
                            <label>Amount Due Date (Optional):</label>
                            <input 
                                type='date' 
                                value={amountDueDate} 
                                onChange={e => setAmountDueDate(e.target.value)} 
                                title="If selected, this date will appear in 'AMOUNT DUE @ [Date]'. If empty, no date or @ will be shown."
                            />
                        </div>
                        <div>
                            <label>Authorized By (Signature):</label>
                            <select value={authorizedBy} onChange={e => setAuthorizedBy(e.target.value)}>
                                <option value="MD. Morshed">MD. Morshed</option>
                                <option value="Anis Ahmed">Anis</option> 
                            </select>
                        </div>
                    </div>
                </div>
                
                <button type='submit' className='save-invoice-btn' disabled={loading || paymentEntries.length === 0}>
                    {loading ? 'Saving...' : 'Save Payment & Print Receipt'}
                </button>
                {message && <p style={{textAlign: 'center', marginTop: '10px'}}>{message}</p>}
            </form>

            <div className='form-section' style={{marginTop: '30px'}}>
                <h3>Recent Payments</h3>
                <div className="search-box-container" style={{ margin: '10px 0 20px 0' }}>
                    <input type="text" placeholder="Search by Receipt No..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} style={{ width: '100%', padding: '10px 12px', fontSize: '15px' }} />
                </div>
                <table className='invoice-preview-table' style={{fontSize: '14px'}}>
                    <thead><tr><th>Receipt No</th><th>Date</th><th>Company</th><th>Bill Month</th><th>Amount</th><th>Method</th><th>Actions</th></tr></thead>
                    <tbody>
                        {listLoading && <tr><td colSpan="7">Loading payments...</td></tr>}
                        {!listLoading && filteredPayments.length === 0 && (<tr><td colSpan="7">{searchQuery ? 'No payments found matching...' : 'No payments found.'}</td></tr>)}
                        {filteredPayments.map(payment => (
                            <tr key={payment.id}>
                                <td>{payment.receiptNo || 'N/A'}</td>
                                <td>{payment.date || (payment.paymentTimestamp ? new Date(payment.paymentTimestamp.toDate()).toLocaleDateString('en-GB') : 'N/A')}</td>
                                <td>{payment.companyName}</td><td>{payment.billMonth}</td><td style={{textAlign: 'right'}}>{formatCurrency(payment.totalAmount || payment.amount)}</td> 
                                <td>{payment.paymentEntries ? `${payment.paymentEntries.length} entries` : payment.method}</td>
                                <td className='action-buttons' style={{gap: '8px'}}><Link to={`/dashboard/payments/view/${payment.id}`} className='view-btn'>View</Link><Link to={`/dashboard/payments/edit/${payment.id}`} className='edit-btn'>Edit</Link></td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    )
}

export default Payments;